import * as React from 'react'
import clsx from 'clsx'
import * as plume from '@ulule/owl-kit-components/next'

import { type Logo } from '../content'
import styles from './LogoCard.module.css'

type Mode = 'light' | 'dark'

export type LogoCardProps = {
  logo: Logo
}

/**
 * One downloadable logo: a light/dark background toggle previewing the matching SVG, plus a download
 * row (PNG · SVG). Every button downloads whichever background variant is currently shown.
 *
 * Interactive (useState), so it hydrates on the client. Initial mode is deterministic ('light') so the
 * SSR HTML matches hydration.
 */
export function LogoCard({ logo }: LogoCardProps): React.ReactElement {
  const [mode, setMode] = React.useState<Mode>('light')

  const activeSvg = logo.svg[mode]
  const activePng = logo.png[mode]

  return (
    <article className={styles.card} data-mode={mode}>
      <div className={styles.card__head}>
        <p className={styles.card__name}>{logo.name}</p>
        <div className={styles.seg} role="group" aria-label="Fond">
          {(['light', 'dark'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={clsx(styles.seg__btn, mode === value && styles.segActive)}
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
            >
              {value === 'light' ? 'Clair' : 'Sombre'}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.card__stage}>
        <img src={activeSvg} alt={`Logo Ulule — ${logo.name}`} style={{ maxWidth: logo.maxWidth }} />
      </div>

      <div className={styles.dlRow}>
        <a className={clsx(styles.dl, styles.dlPrimary)} href={activePng} download={`ulule-${logo.id}-${mode}.png`}>
          <plume.glyphs.stroke.Download size={15} />
          png
        </a>

        <a className={styles.dl} href={activeSvg} download={`ulule-${logo.id}-${mode}.svg`}>
          <plume.glyphs.stroke.CodeBlock size={15} />
          svg
        </a>
      </div>
    </article>
  )
}
